/**
 * ClientSearchField — the workflow surfaces' client search: ora's ONE client
 * search control (`ClientQuicklinkDropdown`, the header search) in its compact,
 * in-page form, speaking the workflow `ClientSearchResult` contract.
 *
 * Story 28633 retired the workflow-only `ClientSearchDropdown`. The client box
 * (`ClientConfirmSection`) and the client tile (`ClientContextTile`) both render
 * this, so the adapter and the layout live once:
 *   - rows show city/state/zip, location and client type (ora's row), so two
 *     same-named clients read differently;
 *   - `compact` sizes it to the pane's one control height and keeps it in the
 *     page's flow (the header's fixed height let it overlap what followed).
 *
 * Shared — no Office.js, no Remix import.
 */
import ClientQuicklinkDropdown from "~/common/components/ClientQuicklinkDropdown";
import type { ClientSearchSource } from "~/common/workflows/clientPicker/clientSearchSource";
import type { ClientSearchResult } from "~/common/workflows/types/client";

/** ora's header prompt, word for word (the control's legacy prompt; kept for importers). */
export const CLIENT_SEARCH_PROMPT = "Start typing client name or address";

export interface ClientSearchFieldProps {
  id: string;
  /**
   * Story 28998: where rows come from (name leg, optional policy leg). Wins
   * over `searchClients` when both are given.
   */
  source?: ClientSearchSource;
  /** Legacy, name leg only: wrapped into a source with no `searchByPolicy`. */
  searchClients?: (query: string) => Promise<ClientSearchResult[]>;
  /** A picked client. A cleared input is not a pick and never calls this. */
  onPick: (result: ClientSearchResult) => void;
  /** The row that carries the check. */
  selectedSk?: number | null;
  /** "Possible matches from this email". */
  candidates?: ClientSearchResult[];
  /** The highlighted row, null when the list closes. */
  onHighlight?: (row: ClientSearchResult | null) => void;
  /** List in the card's flow instead of the floating popup. */
  inline?: boolean;
  /** Only when the person just asked to search (e.g. "Change"); never on load. */
  autoFocus?: boolean;
}

/** The legacy `searchClients` prop as a source with no policy leg. */
const nameOnlySource = (
  searchClients: (query: string) => Promise<ClientSearchResult[]>
): ClientSearchSource => ({ searchByName: searchClients });

/** `source` wins; else the legacy function; neither is a programming error. */
export function resolveClientSearchSource(
  source: ClientSearchSource | undefined,
  searchClients: ClientSearchFieldProps["searchClients"]
): ClientSearchSource {
  if (source) return source;
  if (searchClients) return nameOnlySource(searchClients);
  throw new Error("ClientSearchField needs a `source` or `searchClients`");
}

export function ClientSearchField({
  id,
  source,
  searchClients,
  onPick,
  selectedSk,
  candidates,
  onHighlight,
  inline,
  autoFocus = false,
}: ClientSearchFieldProps) {
  // The prompt follows the source: the picker prompt when it has a policy leg,
  // the legacy one when it does not (the control decides).
  const resolved = resolveClientSearchSource(source, searchClients);
  return (
    <ClientQuicklinkDropdown
      id={id}
      compact
      autoFocus={autoFocus}
      source={resolved}
      candidates={candidates}
      selectedSk={selectedSk}
      onHighlight={onHighlight}
      inline={inline}
      onPick={onPick}
    />
  );
}

export default ClientSearchField;
